import { useEffect, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Col, Container, Row } from 'react-bootstrap';
import { StandardInput } from '../components/forms';
import { PrivatePage } from '../components/private-page';
import { TextareaInput } from '../components/form-input';
import {
  InputChangeEvent,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { appStore } from '../stores/app-store';
import { GameResponse } from '../types/responses/game-response';
import { AddGamesAsync } from '../services/games';
import { GameDocumentVersionResponse } from '../types/responses/game-document-version-response';
import {
  AddGameDocumentAsync,
  AddGameDocumentContentAsync
} from '../services/json-document';
import { GenerateGameDocument } from '../utils/game-document/factory';
import { ExtractBlobResourcesAsync } from '../utils/game-document';
import { Label } from '@progress/kendo-react-labels';
import { useAuth } from 'react-oidc-context';
import Toolbar from '../components/page-toolbar';
import { Coordinate } from '../types/place';
import { FieldWrapper } from '@progress/kendo-react-form';
import LanguageComboBox from '../components/combobox/language-combobox';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import HelpSupport from '../components/help-support';
import { formatDate } from '@progress/kendo-intl';
import {
  DisplayLanguagePublished,
  GetDisplayLanguagePublished
} from '../services/admin-display-language';
import { useToast } from '../contexts/toast-context';

interface NewGame {
  name: string;
  shortDescrption: string;
  languages: string;
  displayLanguages: string;
  title: string;
  isValid: boolean;
}

interface RequiredInput {
  name: string;
  errorMessage: string;
  minLength?: number;
  maxLength?: number;
  isError: boolean;
}

const MyLibraryNew = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [newGame, setNewGame] = useState<NewGame>({
    name: '',
    shortDescrption: '',
    languages: '',
    displayLanguages: '',
    title: '',
    isValid: false
  });
  const [coordinate, setCoordinate] = useState<Coordinate>();
  const [requiredInputs, setRequiredInputs] = useState<RequiredInput[]>([
    { name: 'Name', errorMessage: 'Input Name is required', isError: true },
    {
      name: 'Short description',
      errorMessage: 'Input Description is required',
      maxLength: 256,
      isError: true
    },
    {
      name: 'languages',
      errorMessage: 'Input Game Language is required',
      isError: true
    },
    {
      name: 'displayLanguages',
      errorMessage: 'Input Display Language is required',
      isError: true
    },
    { name: 'Title', errorMessage: 'Input Game Title is required', isError: true }
  ]);
  const [displayLanguages, setDisplayLanguages] = useState<
    DisplayLanguagePublished[]
  >([]);

  const { onToastOpen } = useToast();

  const validateInput = (name?: string, value?: string | number | string[]) => {
    if (isEmpty(name)) {
      setRequiredInputs((prev) =>
        prev.map((item) => {
          let isError = item.isError;
          if (item.name === 'Name') isError = isEmpty(newGame.name);
          if (item.name === 'Short description')
            isError = isEmpty(newGame.shortDescrption);
          if (item.name === 'languages') isError = isEmpty(newGame.languages);
          if (item.name === 'displayLanguages')
            isError = isEmpty(newGame.displayLanguages);
          if (item.name === 'Title') isError = isEmpty(newGame.title);
          return { ...item, isError };
        })
      );
      return;
    }
    setRequiredInputs((prev) =>
      prev.map((item) =>
        item.name === name
          ? {
              ...item,
              isError: isEmpty(value)
            }
          : item
      )
    );
  };

  const handleTextChange = (event: InputChangeEvent) => {
    const { name, value } = event.target;
    validateInput(name, value);
    if (name === 'Name') {
      setNewGame((prev) => ({
        ...prev,
        name: value as string
      }));
    } else if (name === 'Title') {
      setNewGame((prev) => ({
        ...prev,
        title: value as string
      }));
    }
  };

  const handleTextAreaChange = (event: TextAreaChangeEvent) => {
    const { name, value } = event.target;
    validateInput(name!, value);
    if (name === 'Short description') {
      setNewGame((prev) => ({
        ...prev,
        shortDescrption: value as string
      }));
    }
  };

  const onUpdateLocalisations = (event: ComboBoxChangeEvent) => {
    const { name, value } = event.target as {
      name: keyof NewGame;
      value: string;
    };
    validateInput(name as string, value);
    setNewGame((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const mapStateToGameDocument = async (state: NewGame) => {
    let newGameDocument = await GenerateGameDocument();
    newGameDocument.name = state.name;
    newGameDocument.description = state.shortDescrption;
    newGameDocument.author = `${auth.user?.profile.name ?? ''} <${
      auth.user?.profile.email ?? ''
    }>`;
    newGameDocument.resources[0].value = state.title;
    newGameDocument.resources[1].value = state.shortDescrption;
    newGameDocument.resources[2].value = '';
    newGameDocument.resources[2].type = 'text-html';

    if (!isEmpty(coordinate)) {
      newGameDocument.assets.maps![0].latitude = coordinate.lat;
      newGameDocument.assets.maps![0].longitude = coordinate.lng;
    }

    newGameDocument.overview!.languages = state.languages;

    return newGameDocument;
  };

  const onSaveHandler = async () => {
    validateInput();
    const hasError =
      isEmpty(newGame.name) ||
      isEmpty(newGame.shortDescrption) ||
      isEmpty(newGame.languages) ||
      isEmpty(newGame.displayLanguages) ||
      isEmpty(newGame.title) ||
      requiredInputs.some((item) => item.isError);

    if (hasError) {
      return;
    }

    try {
      appStore.showLoading();
      const gameRequest = {
        id: 0,
        type: 'game',
        source: 'created',
        status: 'new',
        name: newGame.name,
        shortDescription: newGame.shortDescrption,
        longDescription: '',
        languages: newGame.languages
      } as GameResponse;

      const gameResponse = await AddGamesAsync(gameRequest);
      if (gameResponse && gameResponse!.id! > 0) {
        const dateString = formatDate(new Date(), 'yyMMdd');
        const fileNamePrefix = `${gameResponse!.id}-${dateString}-0.0.1`;
        const gameDocumentRequest = {
          gameId: gameResponse!.id,
          status: 'new',
          version: '0.0.1',
          fileName: `${fileNamePrefix}.json`
        } as GameDocumentVersionResponse;

        const gameDocumentVersionResponse = await AddGameDocumentAsync(
          gameResponse!.id!,
          gameDocumentRequest
        );

        if (
          gameDocumentVersionResponse &&
          gameDocumentVersionResponse!.id! > 0
        ) {
          const gameDocument = await mapStateToGameDocument(newGame);

          let updateGameDocByExtractBlob = await ExtractBlobResourcesAsync(
            fileNamePrefix,
            gameDocument
          );
          updateGameDocByExtractBlob.version =
            gameDocumentVersionResponse.version!;
          updateGameDocByExtractBlob!.overview!.displayLanguages =
            newGame.displayLanguages;
          updateGameDocByExtractBlob!.overview!.displayLanguagesUrl =
            displayLanguages.find(
              (i) => i.languageName === newGame.displayLanguages
            )?.blobFileUrl;

          await AddGameDocumentContentAsync(
            gameResponse!.id!,
            gameDocumentVersionResponse!.id!,
            updateGameDocByExtractBlob
          );

          navigate(`../../designer/${+gameResponse!.id!.toString()}`);
        }
      }
    } catch (ex) {
      onToastOpen({
        title: 'My library',
        body: <div>Failed to save game.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onCancelHandler = () => {
    navigate('/mylibrary/games');
  };

  const getDisplayLanguages = async () => {
    const response = await GetDisplayLanguagePublished();
    setDisplayLanguages(response.data);
  };

  useEffect(() => {
    function getBrowserCoordinate() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((pos) => {
          setCoordinate({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
        });
      } else {
        alert('Geolocation is not supported by this browser.');
      }
    }
    getDisplayLanguages();
    getBrowserCoordinate();
  }, []);

  return (
    <PrivatePage
      breadcrumb={[
        { label: 'Dashboard', href: '../dashboard' },
        { label: 'My Library', href: '../mylibrary/games' },
        { label: 'New Game', href: '../new' }
      ]}
      pageTitle={'My library'}>
      <>
        <Toolbar title={'New game'}>
          <HelpSupport
            title="Creating a game"
            url="https://forum.catalystglobal.com/t/4522"
          />
          <Button
            themeColor={'secondary'}
            onClick={onCancelHandler}
            className={'me-1'}>
            Back
          </Button>
          <Button themeColor={'primary'} onClick={onSaveHandler}>
            Create
          </Button>
        </Toolbar>
        <hr />
        <Container>
          <Row className={'mt-2 gy-3'}>
            <StandardInput
              label={'Name*'}
              name={'Name'}
              value={newGame.name}
              onChange={handleTextChange}
              validationMessage={
                requiredInputs?.find(
                  (item) => item.name === 'Name' && item.isError
                )?.errorMessage
              }
              maxLength={256}
            />

            <Col xs={12}>
              <TextareaInput
                label={'Description*'}
                name={'Short description'}
                value={newGame.shortDescrption}
                onChange={handleTextAreaChange}
                autoSize
                maxLength={256}
                error={
                  requiredInputs?.find(
                    (item) =>
                      item.name === 'Short description' && item.isError
                  )?.errorMessage
                }
              />
            </Col>

            <Col md={6} xs={12}>
              <FieldWrapper>
                <Label>Game Language*</Label>
                <LanguageComboBox
                  isDisplayLanguages={true}
                  name={'languages'}
                  onChange={(e) => onUpdateLocalisations(e)}
                  clearButton={false}
                  value={newGame?.languages}
                  validationMessage={
                    requiredInputs?.find(
                      (item) => item.name === 'languages' && item.isError
                    )?.errorMessage
                  }
                />
              </FieldWrapper>
            </Col>

            <Col md={6} xs={12}>
              <FieldWrapper>
                <Label>Display Language*</Label>
                <LanguageComboBox
                  isDisplayLanguages={true}
                  name={'displayLanguages'}
                  onChange={(e) => onUpdateLocalisations(e)}
                  clearButton={false}
                  value={newGame?.displayLanguages}
                  validationMessage={
                    requiredInputs?.find(
                      (item) =>
                        item.name === 'displayLanguages' && item.isError
                    )?.errorMessage
                  }
                />
              </FieldWrapper>
            </Col>

            <StandardInput
              label={'Game Title*'}
              name={'Title'}
              value={newGame.title}
              onChange={handleTextChange}
              validationMessage={
                requiredInputs?.find(
                  (item) => item.name === 'Title' && item.isError
                )?.errorMessage
              }
              maxLength={256}
            />
          </Row>
        </Container>
      </>
    </PrivatePage>
  );
};

export default MyLibraryNew;
